import {useCallback, useEffect, useMemo, useState} from "react";
import {TowersSportsApi} from "../../../apis/towers-sports-api/towers-sports-api";
import {LeagueEvent, StremioMetaDetails, StremioStream} from "../../../apis/towers-sports-api/types";
import {useAcestreamActiveConnections} from "../../../hooks/useAcestreamActiveConnections";

export const isLeagueGameFinished = (event: LeagueEvent) => {
    const currentTime = Math.floor(Date.now() / 1000);
    const startTime = new Date(event.strTimestamp + "Z").getTime() / 1000;
    return startTime + 7200 < currentTime;
};

export const isLeagueGameStarted = (event: LeagueEvent) => {
    const currentTime = Math.floor(Date.now() / 1000);
    const startTime = new Date(event.strTimestamp + "Z").getTime() / 1000;
    return startTime - 900 < currentTime;
};

export const leagueGameStartsToday = (event: LeagueEvent) => {
    const eventDate = new Date(event.strTimestamp + "Z");
    const currentDate = new Date();
    return (
        !isLeagueGameStarted(event) &&
        eventDate.getUTCFullYear() === currentDate.getUTCFullYear() &&
        eventDate.getUTCMonth() === currentDate.getUTCMonth() &&
        eventDate.getUTCDate() === currentDate.getUTCDate()
    );
};

export const useLiveChannelDetailsData = (id: string) => {
    const [search, setSearch] = useState("");
    const [selectedProvider, setSelectedProvider] = useState("");
    const [channel, setChannel] = useState<StremioMetaDetails | null>(null);
    const [streams, setStreams] = useState<StremioStream[]>([]);
    const [loading, setLoading] = useState(true);
    const {activeConnections} = useAcestreamActiveConnections();

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        TowersSportsApi.getAcestreamChannelById(id)
            .then(response => {
                setChannel(response);
                return TowersSportsApi.getAcestreamChannelStreams(id).then(streamsResponse => {
                    setStreams(streamsResponse?.streams || []);
                });
            })
            .catch(() => {})
            .finally(() => {
                setLoading(false);
            });
    }, [id]);

    const channelViewers = useMemo(() => {
        if (!channel) return 0;
        const channelStreamHashes = streams.map(s => s.hash);
        return channelStreamHashes.reduce((total, hash) => {
            return total + (activeConnections.connections?.[hash]?.length || 0);
        }, 0);
    }, [channel, activeConnections, streams]);

    const providers = useMemo(() => {
        const uniqueProviders = Array.from(
            new Set(
                streams
                    .map(s => s.name)
                    .map(v => v?.split("\n")[0])
                    .filter(Boolean)
            )
        );
        return [
            {label: "Todos los proveedores", value: ""},
            ...uniqueProviders.map(p => ({label: p, value: p}))
        ];
    }, [streams]);

    const filteredStreams = useMemo(() => {
        return streams.filter(stream => {
            const searchLower = search.toLowerCase();
            const matchesSearch =
                (stream.description || "").toLowerCase().includes(searchLower) ||
                (stream.hash || "").toLowerCase().includes(searchLower);
            const matchesProvider = selectedProvider ? stream.name?.includes(selectedProvider) : true;
            return matchesSearch && matchesProvider;
        });
    }, [streams, search, selectedProvider]);

    const getStreamViewers = useCallback(
        (hash: string) => activeConnections.connections?.[hash]?.length || 0,
        [activeConnections]
    );

    return {
        channel,
        streams,
        filteredStreams,
        providers,
        search,
        setSearch,
        selectedProvider,
        setSelectedProvider,
        channelViewers,
        getStreamViewers,
        loading
    };
};

export default useLiveChannelDetailsData;
